import * as React from 'react';
import type { MenuProps } from 'antd';
import { Button, Dropdown } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import type { DynamicComponentProps } from '@/types/commontypes';
import { VertexType } from '@/types/commontypes';
import LeftNormalLine from '@/assets/svg/left_normal_line.svg';
import styles from '@/styles/global.module.css';

const ArrowOption: React.FC<DynamicComponentProps<[VertexType, VertexType]>> = ({
  label,
  value = [VertexType.NORMAL, VertexType.NORMAL],
  onChange,
}) => {
  const [localSelection, setLocalSelection] = useState(value);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  useEffect(() => {
    setLocalSelection(value);
  }, [value]);

  const handleMenuItemSelect = (side: 'left' | 'right', e: { key: string }) => {
    const selectedValue = e.key === 'arrow-line' ? VertexType.ARROW : VertexType.NORMAL;
    const idx = side === 'left' ? 0 : 1;
    const updatedSelection: [VertexType, VertexType] = [...localSelection];
    updatedSelection[idx] = selectedValue;
    setLocalSelection(updatedSelection);
    onChange(updatedSelection);
  };

  const getMenuItems = useMemo(() => {
    return (side: 'left' | 'right'): MenuProps['items'] => [
      {
        key: 'arrow-line',
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {side === 'left' ? (
              <ArrowLeftOutlined style={{ fontSize: '1rem', width: '28px', justifyContent: 'center' }} />
            ) : (
              <ArrowRightOutlined style={{ fontSize: '1rem', width: '28px', justifyContent: 'center' }} />
            )}
            <span style={{ marginLeft: '8px' }}>Arrow</span>
          </div>
        ),
      },
      {
        key: 'normal-line',
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <LeftNormalLine title="icon" className={side === 'right' ? 'rotate-180' : ''} />
            <span style={{ marginLeft: '8px' }}>Normal</span>
          </div>
        ),
      },
    ];
  }, []);

  const menuLeft: MenuProps = {
    items: getMenuItems('left'),
    onClick: e => handleMenuItemSelect('left', e),
  };

  const menuRight: MenuProps = {
    items: getMenuItems('right'),
    onClick: e => handleMenuItemSelect('right', e),
  };

  const getButtonContent = (side: 'left' | 'right') => {
    const selectedValue = localSelection[side === 'left' ? 0 : 1];
    if (selectedValue === VertexType.ARROW) {
      return side === 'left' ? <ArrowLeftOutlined /> : <ArrowRightOutlined />;
    } else {
      return <LeftNormalLine title="icon" className={side === 'right' ? 'rotate-180' : ''} />;
    }
  };

  return (
    <div className={styles.property_row}>
      <label className={styles.property_label}>{label}</label>
      <div className={styles.property_value}>
        <Dropdown menu={menuLeft} open={leftOpen} onOpenChange={setLeftOpen} trigger={['click']} placement="bottomLeft">
          <Button type="default" icon={getButtonContent('left')} size="large" />
        </Dropdown>

        <Dropdown
          menu={menuRight}
          open={rightOpen}
          onOpenChange={setRightOpen}
          trigger={['click']}
          placement="bottomLeft"
        >
          <Button type="default" icon={getButtonContent('right')} size="large" />
        </Dropdown>
      </div>
    </div>
  );
};

export default ArrowOption;
